import type { MetricValues as CoreMetricValues } from '@revelio/core';
import {
  groupedMetricOptions,
  sentimentGroupedMetricOptions,
} from '@revelio/core/dashboard-capabilities';
import type {
  CompositionV3RespData,
  CompositionV4RespData,
  SentimentMetrics,
} from '@revelio/data-access';

export type CompositionMetricValues = keyof Pick<
  CompositionV3RespData,
  | 'headcount'
  | 'growth_rate'
  | 'hiring_rate'
  | 'attrition_rate'
  | 'tenure'
  | 'salary'
>;

export type CompositionV4Metric = keyof Omit<
  CompositionV4RespData,
  '__typename'
>;

const compositionV4Metrics: Record<CompositionV4Metric, true> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
};

export const isCompositionV4Metric = (
  metric?: MetricValues
): metric is CompositionV4Metric =>
  compositionV4Metrics[metric as CompositionV4Metric] === true;

const compositionMetrics: Record<CompositionMetricValues, boolean> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
};
export const isCompositionMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues =>
  compositionMetrics[metric as CompositionMetricValues];

export const isCompositionRoutableMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues => isCompositionMetric(metric);

export const isNonHeadcountCompositionMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues =>
  compositionMetrics[metric as CompositionMetricValues] &&
  metric !== 'headcount';

export type TransitionMetricValues =
  | 'transitions_inflow'
  | 'transitions_outflow'
  | 'transitions_inflows_and_outflows';
const transitionMetrics: Record<TransitionMetricValues, boolean> = {
  transitions_inflow: true,
  transitions_outflow: true,
  transitions_inflows_and_outflows: true,
};
export const isTransitionMetric = (
  metric?: MetricValues
): metric is TransitionMetricValues =>
  transitionMetrics[metric as TransitionMetricValues];

export type PostingMetricValues =
  | 'posting_salary'
  | 'time_to_fill'
  | 'time_to_fill_percentile'
  | 'postings_active'
  | 'postings_new'
  | 'postings_removed'
  | 'postings_volume';
const postingMetrics: Record<PostingMetricValues, boolean> = {
  posting_salary: true,
  time_to_fill: true,
  time_to_fill_percentile: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  postings_volume: true,
};
export const isPostingMetric = (
  metric?: MetricValues
): metric is PostingMetricValues =>
  postingMetrics[metric as PostingMetricValues];

export type PayMetricValues = Extract<
  MetricValues,
  | 'total_compensation'
  | 'base_salary'
  | 'contractor_pay_rate'
  | 'total_spend_salary'
  | 'total_spend_compensation'
  | 'base_salary_percentiles'
  | 'total_compensation_percentiles'
  | 'contractor_pay_rate_percentiles'
>;
const payMetrics: Record<PayMetricValues, boolean> = {
  total_compensation: true,
  base_salary: true,
  contractor_pay_rate: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
};
export const isPayMetric = (metric?: MetricValues): metric is PayMetricValues =>
  payMetrics[metric as PayMetricValues];

export type PayPercentileMetricValues = Extract<
  PayMetricValues,
  | 'base_salary_percentiles'
  | 'total_compensation_percentiles'
  | 'contractor_pay_rate_percentiles'
>;
const payPercentileMetrics: Record<PayPercentileMetricValues, boolean> = {
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
};
export const isPayPercentileMetric = (
  metric?: MetricValues
): metric is PayPercentileMetricValues =>
  payPercentileMetrics[metric as PayPercentileMetricValues];

export type SentimentMetricKeys = keyof Pick<
  SentimentMetrics,
  | 'overall'
  | 'business_outlook'
  | 'career_growth'
  | 'compensation'
  | 'benefits'
  | 'healthcare'
  | 'retirement_pension'
  | 'childcare'
  | 'education_assistance'
  | 'wellness_programs'
  | 'work_life_balance'
  | 'diversity_and_inclusion'
  | 'leadership'
  | 'culture'
  | 'remote_work'
  | 'onboarding'
  | 'mentorship'
  | 'technology'
  | 'promotion_opportunities'
  | 'paid_time_off'
  | 'transparency'
  | 'efficiency_and_bureaucracy'
  | 'product_and_service_quality'
  | 'prestige'
  | 'coworkers'
  | 'mission_and_vision'
  | 'workplace_facilities'
  | 'training'
  | 'clients_and_customers'
  | 'company_growth'
  | 'schedule_flexibility'
  | 'job_security'
  | 'overtime'
  | 'family_support'
  | 'commission'
  | 'independence'
  | 'commute'
  | 'safety'
  | 'internal_communication'
  | 'office_politics'
  | 'harassment'
  | 'honesty'
  | 'ethics'
  | 'commitment'
  | 'compliance'
  | 'integrity'
>;

export type SentimentMetricValues =
  | SentimentMetricKeys
  | 'sentiment_top_bottom';

const sentimentMetrics: Record<SentimentMetricValues, boolean> = {
  sentiment_top_bottom: true,
  overall: true,
  business_outlook: true,
  career_growth: true,
  compensation: true,
  benefits: true,
  healthcare: true,
  retirement_pension: true,
  childcare: true,
  education_assistance: true,
  wellness_programs: true,
  work_life_balance: true,
  diversity_and_inclusion: true,
  leadership: true,
  culture: true,
  remote_work: true,
  onboarding: true,
  mentorship: true,
  technology: true,
  promotion_opportunities: true,
  paid_time_off: true,
  transparency: true,
  efficiency_and_bureaucracy: true,
  product_and_service_quality: true,
  prestige: true,
  coworkers: true,
  mission_and_vision: true,
  workplace_facilities: true,
  training: true,
  clients_and_customers: true,
  company_growth: true,
  schedule_flexibility: true,
  job_security: true,
  overtime: true,
  family_support: true,
  commission: true,
  independence: true,
  commute: true,
  safety: true,
  internal_communication: true,
  office_politics: true,
  harassment: true,
  honesty: true,
  ethics: true,
  commitment: true,
  compliance: true,
  integrity: true,
};
export const isSentimentMetric = (
  metric?: MetricValues
): metric is SentimentMetricValues =>
  sentimentMetrics[metric as SentimentMetricValues];

// Screener metrics
export type ScreenerMetricValues =
  | 'screener_headcount'
  | 'screener_growth'
  | 'screener_growth_yoy'
  | 'screener_hiring'
  | 'screener_attrition'
  | 'screener_salary'
  | 'screener_tenure'
  | 'screener_inflow'
  | 'screener_outflow';

const screenerMetrics: Record<ScreenerMetricValues, boolean> = {
  screener_headcount: true,
  screener_growth: true,
  screener_growth_yoy: true,
  screener_hiring: true,
  screener_attrition: true,
  screener_salary: true,
  screener_tenure: true,
  screener_inflow: true,
  screener_outflow: true,
};

export const isScreenerMetric = (
  metric: MetricValues
): metric is ScreenerMetricValues =>
  screenerMetrics[metric as ScreenerMetricValues];

// Talent Discovery metrics
export type TalentDiscoveryMetricValues = 'td_users';

const talentDiscoveryMetrics: Record<TalentDiscoveryMetricValues, boolean> = {
  td_users: true,
};

export const isTalentDiscoveryMetric = (
  metric?: MetricValues
): metric is TalentDiscoveryMetricValues =>
  talentDiscoveryMetrics[metric as TalentDiscoveryMetricValues];

// Import base type from core and re-export for backward compatibility
export type MetricValues = CoreMetricValues;

export type { GroupedMetricOption, MetricOption } from '@revelio/core';
export { groupedMetricOptions, sentimentGroupedMetricOptions };

export const isFromGroup = (groupName: string, metric: MetricValues) => {
  return groupedMetricOptions
    .find((group) => group.label === groupName)
    ?.options.some((option) => option.value === metric);
};

export const metricOptions = groupedMetricOptions.flatMap(
  (groupedOption) => groupedOption.options
);
